import React from "react";
import { Button, Text } from "braid-design-system";
import type { LineColumnConfig, TransactionLine } from "./transactionScreenTypes";
import { isZeroNumericString, sanitizeNumericString } from "../../utils/numericInput";

type TransactionItemsSectionProps = {
    visibleLineColumns: LineColumnConfig[];
    lines: TransactionLine[];
    isReadOnlyProcessedDocument: boolean;
    stagedLineIds: Record<number, boolean>;
    stagingLineId: number | null;
    onAddLine: () => void;
    onSearchItems: () => void;
    onLineFocus: (lineId: number) => void;
    onStageLine: (line: TransactionLine, index: number) => void;
    onRemoveLine: (lineId: number) => void;
    onProductCodeChange: (lineId: number, productCode: string) => void;
    onProductCodeSubmit: (lineId: number, productCode: string) => void;
    onUpdateLine: (lineId: number, key: keyof TransactionLine, value: string) => void;
};

const TrashIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={size} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" stroke="currentColor">
        <path d="M3 6h18" />
        <path d="M8 6V4h8v2" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M6 6l1 14h10l1-14" />
    </svg>
);

const TickIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={size} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" stroke="currentColor">
        <path d="M20 6L9 17l-5-5" />
    </svg>
);

const TransactionItemsSection: React.FC<TransactionItemsSectionProps> = ({
    visibleLineColumns,
    lines,
    isReadOnlyProcessedDocument,
    stagedLineIds,
    stagingLineId,
    onAddLine,
    onSearchItems,
    onLineFocus,
    onStageLine,
    onRemoveLine,
    onProductCodeChange,
    onProductCodeSubmit,
    onUpdateLine,
}) => {
    return (
        <section className="transaction-section">
            <div className="transaction-section-heading">
                <Text weight="strong">Items</Text>
                <div className="transaction-table-actions">
                    <Button size="small" variant="solid" tone="formAccent" onClick={isReadOnlyProcessedDocument ? undefined : onAddLine}>
                        + Add Line
                    </Button>
                    <Button size="small" variant="ghost" tone="brandAccent" onClick={isReadOnlyProcessedDocument ? undefined : onSearchItems}>
                        Search Items
                    </Button>
                </div>
            </div>
            <div className="transaction-table-wrap">
                <table className="transaction-table">
                    <thead>
                        <tr>
                            <th>No</th>
                            {visibleLineColumns.map((column) => (
                                <th key={column.key}>{column.label}</th>
                            ))}
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((line, index) => (
                            <tr key={line.id}>
                                <td>{index + 1}</td>
                                {visibleLineColumns.map((column) => (
                                    <td key={column.key}>
                                        <input
                                            disabled={isReadOnlyProcessedDocument || column.editable === false}
                                            type={column.type || "text"}
                                            inputMode={column.type === "number" ? "decimal" : undefined}
                                            step={column.type === "number" ? 10 ** -(column.decimalPlaces ?? 2) : undefined}
                                            title={column.label}
                                            aria-label={column.label}
                                            placeholder={column.key === "productCode" ? "Barcode / code / name" : column.label}
                                            data-line-id={line.id}
                                            data-column-key={column.key}
                                            value={String(line[column.key as keyof TransactionLine] ?? "")}
                                            onFocus={() => {
                                                onLineFocus(line.id);

                                                if (column.type === "number" && column.editable !== false) {
                                                    const current = String(line[column.key as keyof TransactionLine] ?? "");
                                                    if (isZeroNumericString(current)) {
                                                        onUpdateLine(line.id, column.key as keyof TransactionLine, "");
                                                    }
                                                }
                                            }}
                                            onChange={(event) => {
                                                if (column.editable !== false) {
                                                    if (column.key === "productCode") {
                                                        onProductCodeChange(line.id, event.target.value);
                                                    } else if (column.type === "number") {
                                                        onUpdateLine(
                                                            line.id,
                                                            column.key as keyof TransactionLine,
                                                            sanitizeNumericString(event.target.value, {
                                                                allowNegative: true,
                                                                decimalPlaces: column.decimalPlaces,
                                                            }),
                                                        );
                                                    } else {
                                                        onUpdateLine(line.id, column.key as keyof TransactionLine, event.target.value);
                                                    }
                                                }
                                            }}
                                            onKeyDown={(event) => {
                                                if (column.key === "productCode" && event.key === "Enter") {
                                                    event.preventDefault();
                                                    onProductCodeSubmit(line.id, event.currentTarget.value);
                                                }
                                            }}
                                        />
                                    </td>
                                ))}
                                <td className="transaction-line-actions-cell">
                                    <div className="transaction-line-actions">
                                        <Button
                                            size="small"
                                            variant="ghost"
                                            tone={stagedLineIds[line.id] ? "formAccent" : "brandAccent"}
                                            icon={<TickIcon />}
                                            aria-label="Stage line"
                                            loading={stagingLineId === line.id}
                                            onClick={isReadOnlyProcessedDocument ? undefined : () => onStageLine(line, index)}
                                        />
                                        <Button
                                            size="small"
                                            variant="ghost"
                                            tone="critical"
                                            icon={<TrashIcon />}
                                            aria-label="Remove line"
                                            onClick={isReadOnlyProcessedDocument ? undefined : () => onRemoveLine(line.id)}
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export default TransactionItemsSection;
